import { useEffect, useState } from 'react'
import { Settings as SettingsIcon, Save, RefreshCw, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react'
import { AppConfig, TelegramDestination } from './types'
import { API_BASE } from '../config'

const emptyDestination = (): TelegramDestination => ({ chat_id: '', thread_id: null, label: '' })

export default function Settings() {
  const [config, setConfig] = useState<AppConfig>({
    target_url: '',
    openrouter_model: '',
    speech_language: '',
    schedule: '',
    ai_system_prompt: '',
    telegram_destinations: [],
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${API_BASE}/config`)
      if (!res.ok) throw new Error('Failed to retrieve system settings')
      const data = await res.json()
      setConfig(data)
    } catch (err: any) {
      setError(err.message || 'Server connection failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchConfig() }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setSuccess(false)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (!res.ok) throw new Error('Failed to save settings')
      const data = await res.json()
      setConfig(data.config)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to update system config')
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setConfig((prev) => ({ ...prev, [name]: value }))
  }

  const updateDestination = (i: number, field: keyof TelegramDestination, value: string) => {
    setConfig((prev) => {
      const dests = [...prev.telegram_destinations]
      if (field === 'thread_id') {
        dests[i] = { ...dests[i], thread_id: value === '' ? null : parseInt(value, 10) || null }
      } else {
        dests[i] = { ...dests[i], [field]: value }
      }
      return { ...prev, telegram_destinations: dests }
    })
  }

  const addDestination = () =>
    setConfig((prev) => ({
      ...prev,
      telegram_destinations: [...prev.telegram_destinations, emptyDestination()],
    }))

  const removeDestination = (i: number) =>
    setConfig((prev) => ({
      ...prev,
      telegram_destinations: prev.telegram_destinations.filter((_, idx) => idx !== i),
    }))

  const inputCls = 'w-full bg-slate-950 border border-slate-850 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors font-mono'

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
          System Parameters
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Adjust runtime scraping variables, schedule configurations, and speech transcription modes.
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-center gap-3">
          <AlertCircle size={18} className="flex-shrink-0" />
          <span><strong>Alert:</strong> {error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-3">
          <CheckCircle size={18} className="flex-shrink-0 text-emerald-400 glow-emerald" />
          <span>Configuration saved successfully. System will apply updates on the next scheduler run.</span>
        </div>
      )}

      {loading ? (
        <div className="glass-panel rounded-2xl p-12 text-center text-slate-500 text-sm flex flex-col items-center gap-3">
          <RefreshCw size={24} className="animate-spin text-cyan-400" />
          Retrieving settings matrix...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="glass-panel rounded-2xl p-6 md:p-8 max-w-3xl space-y-6">
          <div className="flex items-center gap-2 pb-4 border-b border-slate-850">
            <SettingsIcon size={18} className="text-cyan-400" />
            <h3 className="text-base font-bold text-white uppercase tracking-wider">Pipeline Variables</h3>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Scraper Target URL
              </label>
              <input
                type="url"
                name="target_url"
                required
                value={config.target_url}
                onChange={handleChange}
                className={inputCls}
                placeholder="https://bitcoin.live/blogs/premium"
              />
              <p className="text-[10px] text-slate-500 mt-1.5 leading-normal">
                The absolute premium article index endpoint the browser navigates to.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Scraper Schedule (Cron expression)
                </label>
                <input
                  type="text"
                  name="schedule"
                  required
                  value={config.schedule}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="0 */4 * * *"
                />
                <p className="text-[10px] text-slate-500 mt-1.5 leading-normal">
                  Cron trigger pattern. Default triggers every 4 hours.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Speech Language Code
                </label>
                <input
                  type="text"
                  name="speech_language"
                  required
                  value={config.speech_language}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="en-US"
                />
                <p className="text-[10px] text-slate-500 mt-1.5 leading-normal">
                  Google Cloud Speech recognition language tag (e.g. en-US, es-ES).
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                OpenRouter AI Model Name
              </label>
              <input
                type="text"
                name="openrouter_model"
                required
                value={config.openrouter_model}
                onChange={handleChange}
                className={inputCls}
                placeholder="anthropic/claude-3.5-sonnet"
              />
              <p className="text-[10px] text-slate-500 mt-1.5 leading-normal">
                Valid OpenRouter model identifier used for summarization requests.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              AI System Prompt
            </label>
            <textarea
              name="ai_system_prompt"
              rows={6}
              value={config.ai_system_prompt || ''}
              onChange={handleChange}
              className={`${inputCls} resize-y`}
              placeholder="Leave blank to use the default prompt."
            />
            <p className="text-[10px] text-slate-500 mt-1.5 leading-normal">
              Override the default summarization instructions. Blank = built-in default (3–5 bullet points).
            </p>
          </div>

          {/* Telegram Destinations */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                Telegram Destinations
              </label>
              <button
                type="button"
                onClick={addDestination}
                className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                <Plus size={13} />
                Add destination
              </button>
            </div>

            <div className="space-y-3">
              {(config.telegram_destinations || []).map((dest, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_1fr_auto] gap-2 items-center">
                  <input
                    type="text"
                    value={dest.chat_id}
                    onChange={(e) => updateDestination(i, 'chat_id', e.target.value)}
                    placeholder="Chat ID (e.g. -100123...)"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={dest.thread_id ?? ''}
                    onChange={(e) => updateDestination(i, 'thread_id', e.target.value)}
                    placeholder="Thread ID"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={dest.label}
                    onChange={(e) => updateDestination(i, 'label', e.target.value)}
                    placeholder="Label (optional)"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => removeDestination(i)}
                    className="p-2 text-slate-600 hover:text-rose-400 transition-colors"
                    title="Remove"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              {(config.telegram_destinations || []).length === 0 && (
                <p className="text-[11px] text-slate-600 italic">
                  No destinations configured — bot will use the primary chat from environment variables.
                </p>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 leading-normal">
              Each row is a Telegram chat/channel the bot posts to. Thread ID is optional (for forum-style channels). Supergroup chat IDs start with -100.
            </p>
          </div>

          {/* Action button */}
          <div className="pt-6 border-t border-slate-850 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200
                ${saving
                  ? 'bg-slate-900 border border-slate-850 text-slate-500 cursor-not-allowed'
                  : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-[0_0_20px_rgba(6,182,212,0.35)] hover:scale-[1.02]'
                }`}
            >
              {saving ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save System Config
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
